import { defer, catchError, retry, Observable } from 'rxjs';
import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { LogOnLoadAndRefreshGuard } from './tpi-guard/log-on-load-and-refresh.guard';
import { OnlyDevelopmentGuard } from './tpi-guard/only-development.guard';
import { OnlyLoggedInGuard } from './tpi-guard/only-logged-in.guard';
import { OnlyLoggedOutGuard } from './tpi-guard/only-logged-out.guard';
import { OnlySuperAdminUserGuard } from './tpi-guard/only-super-admin-user.guard';
import { OnlySuperUserGuard } from './tpi-guard/only-super-user.guard';
import { OnlyWebSupportGuard } from './tpi-guard/only-web-support.guard';
import { UnsavedChangesGuard } from './tpi-guard/unsaved-changes.guard';

export function loadWithRetry<t>(loader: () => Promise<t>): Observable<t> {
    return defer(() => loader()).pipe(
        retry(environment.lazyLoadRetryCount),
        catchError((error: unknown) => {
            throw new Error(`Loading component failed after ${environment.lazyLoadRetryCount} retries`, { cause: error });
        })
    );
}

export const routes: Routes = [
    {
        path: 'policy-search',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/policy-inquiry/policy-inquiry.component').then((module) => module.PolicyInquiryComponent)),
        canActivate: [OnlyLoggedInGuard],
        data: { showNavigation: true }
    },
    {
        path: 'mortgage-bulk-update',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/mortgage-bulk-update/mortgage-bulk-update.component').then((module) => module.MortgageBulkUpdateComponent)),
        title: 'Mortgage Bulk Update - Nationwide',
        canActivate: [OnlyLoggedInGuard],
        data: { showNavigation: true }
    },
    {
        path: 'manage-users',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/manage-user-accounts/manage-user-accounts.component').then((module) => module.ManageUserAccountsComponent)),
        title: 'Manage User Accounts - Nationwide',
        canActivate: [LogOnLoadAndRefreshGuard, OnlyLoggedInGuard, OnlySuperAdminUserGuard],
        data: { showNavigation: true }
    },
    {
        path: 'manage-user',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/manage-user/manage-user.component').then((module) => module.ManageUserComponent)),
        title: 'Manage User Accounts - Nationwide',
        canActivate: [LogOnLoadAndRefreshGuard, OnlyLoggedInGuard, OnlySuperAdminUserGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: { showNavigation: true }
    },
    {
        path: 'add-user',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/manage-user-accounts/modify-users/add-new-user/add-new-user.component').then((module) => module.AddNewUserComponent)),
        title: 'Manage User Accounts - Nationwide',
        canActivate: [LogOnLoadAndRefreshGuard, OnlyLoggedInGuard, OnlySuperAdminUserGuard],
        data: { showNavigation: true }
    },
    {
        path: 'company-list',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/manage-company-list/manage-company-list.component').then((module) => module.ManageCompanyListComponent)),
        title: 'Manage Company List - Nationwide',
        canActivate: [LogOnLoadAndRefreshGuard, OnlyLoggedInGuard, OnlySuperUserGuard],
        data: { showNavigation: true }
    },
    {
        path: 'web-support',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/manage-user-web/manage-user-web.component').then((module) => module.ManageUserWebComponent)),
        title: 'Manage User Account - Nationwide',
        canActivate: [OnlyLoggedInGuard, OnlyWebSupportGuard],
        data: { showNavigation: true }
    },
    {
        path: 'web-support/davinci-web',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/manage-user-web/manage-user-web-davinci/manage-user-web-davinci.component').then((module) => module.ManageUserWebDavinciComponent)),
        title: 'Manage User Web Davinci - Nationwide',
        canActivate: [OnlyLoggedInGuard, OnlyWebSupportGuard],
        data: { showNavigation: true }
    },
    {
        path: 'forgotid',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/user-credentials/forgot-id/forgot-id.component').then((module) => module.ForgotIdComponent)),
        canActivate: [OnlyDevelopmentGuard],
        data: { showNavigation: false }
    },
    {
        path: 'terms-and-conditions',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/manage-accounts-company-web/terms-and-conditions/terms-and-conditions.component').then((module) => module.TermsAndConditionsComponent)),
        title: 'Terms And Conditions - Nationwide',
        canActivate: [OnlyLoggedInGuard],
        data: { showNavigation: false, deepLink: true }
    },
    {
        path: 'error',
        title: 'Nationwide Policy Inquiry - Error',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/login-logout/login-logout-errors/login-logout-errors.component').then((module) => module.LoginLogoutErrorsComponent)),
        canActivate: [OnlyLoggedOutGuard],
        data: { hideTitle: true }
    },
    {
        path: 'landing',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/login-logout/login-logout-landing/login-logout-landing.component').then((module) => module.LoginLogoutLandingComponent)),
        title: 'Nationwide Policy Inquiry - Landing',
        canActivate: [OnlyLoggedOutGuard],
        data: { hideTitle: true }
    },
    {
        path: 'logout',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/login-logout/logout/logout.component').then((module) => module.LogoutComponent)),
        title: 'Nationwide Policy Inquiry - Logout',
        canActivate: [OnlyLoggedOutGuard],
        data: { showNavigation: false }
    },
    {
        path: 'stub',
        loadComponent: () => loadWithRetry(() => import('./tpi-components/stub/stub.component').then((module) => module.StubComponent)),
        canActivate: [OnlyDevelopmentGuard]
    },
    { path: '', redirectTo: 'landing', pathMatch: 'full' }
];